<div class="quiz-generator-container">
  <!-- LIST VIEW (New Design) -->
  @if (viewMode() === 'LIST') {
    <div class="list-view">
      <div class="header list-header">
        <div class="header-titles">
          <h2>{{ 'Quiz.ManagementTitle' | translate }}</h2>
          <p>{{ 'Quiz.ManagementSubtitle' | translate }}</p>
        </div>
        <div class="header-actions">
          <button mat-icon-button (click)="close.emit()">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      </div>
      <div class="content list-content custom-scrollbar">
        <!-- Create New Section -->
        <div class="section-block">
          <div class="create-cards">
            <!-- AI Card -->
            @if (!cannotUseAI()) {
              <div class="create-card ai-card" [class.disabled]="isLoadingDrafts()" (click)="!isLoadingDrafts() && openAiSetup()">
                <div class="card-icon-wrapper ai-icon-wrapper">
                  <mat-icon>auto_awesome</mat-icon>
                </div>
                <div class="card-info">
                  <h4>{{ 'Quiz.GenerateWithAI' | translate }}</h4>
                  <p>{{ 'Quiz.GenerateAIDesc' | translate }}</p>
                </div>
              </div>
            }
            <!-- Manual Card -->
            <div class="create-card manual-card" [class.disabled]="isLoadingDrafts()" (click)="!isLoadingDrafts() && startNewManual()">
              <div class="card-icon-wrapper manual-icon-wrapper">
                <mat-icon>add</mat-icon>
              </div>
              <div class="card-info">
                <h4>{{ 'Quiz.CreateManual' | translate }}</h4>
                <p>{{ 'Quiz.CreateManualDesc' | translate }}</p>
              </div>
            </div>
          </div>
        </div>
        <!-- Existing Quizzes Section -->
        <mat-divider class="mb-3"></mat-divider>
        <div class="section-block">
          <div class="quiz-list">
            <!-- Draft Items -->
            @for (draft of drafts(); track draft._id) {
              @if (draft.status !== 'setup') {
                <div class="quiz-list-item draft-item" (click)="resumeDraft(draft._id)">
                  <div class="item-icon-wrapper draft-icon-wrapper">
                    <mat-icon>edit_note</mat-icon>
                  </div>
                  <div class="item-content-row">
                    <h4 class="item-title">{{ draft.quizContent?.title || draft.quizContent?.en?.title || ('Quiz.DraftTitle' | translate) }}</h4>
                  </div>
                  <div class="item-actions-row">
                    <button #deleteDraftBtn mat-icon-button class="delete-btn" (click)="deleteDraft(draft._id, deleteDraftBtn); $event.stopPropagation()">
                      <mat-icon>delete_outline</mat-icon>
                    </button>
                  </div>
                </div>
              }
            }
            <!-- Existing Items (White cards now) -->
            @if (canEditExistingQuizzes()) {
              @for (quiz of existingQuizzes(); track quiz._id) {
                <div class="quiz-list-item existing-quiz-card">
                  <div class="item-icon-wrapper">
                    <mat-icon class="quiz-icon">grading</mat-icon>
                  </div>
                  <div class="item-content-row">
                    <h4 class="item-title">{{ quiz.editions[0]?.content?.title || quiz.text }}</h4>
                    <span class="item-date">{{ quiz.createdAt | date: 'dd/MM/yyyy HH:mm' }}</span>
                  </div>
                  <div class="item-actions-row">
                    <button #deleteChunkBtn mat-icon-button class="action-btn delete-btn" (click)="deleteQuizChunk(quiz, deleteChunkBtn); $event.stopPropagation()">
                      <mat-icon>delete_outline</mat-icon>
                    </button>
                  </div>
                </div>
              }
            }
            @if (existingQuizzes().length === 0 && drafts().length === 0) {
              <div class="empty-state">
                <p>{{ 'Quiz.NoExistingQuizzes' | translate }}</p>
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  }

  <!-- Manual Mode Header -->
  @if (viewMode() === 'MANUAL') {
    <div class="header">
      <h2>{{ 'Quiz.CreateTitle' | translate }}</h2>
      <div class="header-actions">
        <div class="autosave-indicator" [class.visible]="isSaving() || showSavedStatus()">
          @if (isSaving()) {
            <mat-spinner diameter="16"></mat-spinner>
            <span>{{ 'Global.Saving' | translate }}</span>
          } @else if (showSavedStatus()) {
            <mat-icon>cloud_done</mat-icon>
            <span>{{ 'Global.Saved' | translate }}</span>
          }
        </div>
        <button mat-icon-button (click)="togglePreview()" [matTooltip]="(isPreviewMode() ? 'Global.Edit' : 'Global.Preview') | translate">
          <mat-icon>{{ isPreviewMode() ? 'edit' : 'visibility' }}</mat-icon>
        </button>
        <button mat-icon-button (click)="cancel()">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>
  }

  <!-- AI Setup Mode Header -->
  @if (viewMode() === 'AI_SETUP') {
    <div class="header">
      <div class="flex items-center gap-2">
        <button mat-icon-button (click)="goBackToList()">
          <mat-icon>arrow_back</mat-icon>
        </button>
        <h2 class="m-0">{{ 'Quiz.AiGeneratorTitle' | translate }}</h2>
      </div>
      <button mat-icon-button (click)="close.emit()">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  }

  <!-- MANUAL CONTENT -->
  @if (viewMode() === 'MANUAL') {
    <div class="content custom-scrollbar" [class.hidden]="isPreviewMode()">
      <form [formGroup]="quizForm">
        <!-- Title -->
        <mat-form-field appearance="fill" class="title-field" subscriptSizing="dynamic">
          <input matInput formControlName="title" [placeholder]="'Quiz.TitlePlaceholder' | translate" />
        </mat-form-field>
        <div formArrayName="questions" class="questions-list">
          @for (question of questions.controls; track question; let i = $index) {
            <div [formGroupName]="i" class="question-card">
              <div class="question-body">
                <span class="question-number">{{ i + 1 }}</span>
                <mat-form-field appearance="fill" class="question-textarea-wrapper" subscriptSizing="dynamic">
                  <textarea matInput cdkTextareaAutosize cdkAutosizeMinRows="1" cdkAutosizeMaxRows="5" formControlName="text" [placeholder]="'Quiz.QuestionPlaceholder' | translate"></textarea>
                </mat-form-field>
                @if (questions.length > 1) {
                  <button mat-icon-button class="delete-question-btn" (click)="removeQuestion(i)">
                    <mat-icon>delete_outline</mat-icon>
                  </button>
                }
              </div>
              <div class="question-type-row flex items-center gap-3 mb-3">
                <label class="subtitle select-type-label m-0 font-medium text-xs text-gray-500">{{ ('Quiz.TypeLabel' | translate) || 'Question Type' }}</label>
                <ui-select formControlName="type" [options]="quizTypeOptions" [compact]="true"></ui-select>
              </div>

              <!-- Options -->
              @if (question.get('type')?.value !== 'free-text') {
                <p class="subtitle">{{ 'Quiz.OptionsLabel' | translate }}</p>
                <div class="options-list" formArrayName="options">
                  @for (option of getOptions(i).controls; track option; let j = $index) {
                    <div [formGroupName]="j" class="option-row">
                      <div class="correct-indicator" (click)="setCorrectOption(i, j)">
                        <ui-checkbox [checked]="option.get('isCorrect').value" (checkedChange)="setCorrectOption(i, j)"></ui-checkbox>
                      </div>
                      <mat-form-field appearance="fill" class="option-input-wrapper" subscriptSizing="dynamic">
                        <input matInput formControlName="label" [placeholder]="('Quiz.OptionPlaceholder' | translate) + ' ' + (j + 1)" />
                      </mat-form-field>
                      @if (getOptions(i).length > 2) {
                        <button mat-icon-button class="remove-option-btn" (click)="removeOption(i, j)">
                          <mat-icon>close</mat-icon>
                        </button>
                      }
                    </div>
                  }
                  <button mat-button class="add-option-btn" (click)="addOption(i)">
                    <mat-icon>add</mat-icon>
                    {{ 'Quiz.AddOption' | translate }}
                  </button>
                </div>
              }
              <!-- Explanation / Ideal Answer -->
              <p class="subtitle">
                {{ getExplanationLabel(question) }}
              </p>
              <div class="explanation-section">
                <mat-form-field appearance="fill" class="explanation-textarea-wrapper" subscriptSizing="dynamic">
                  <textarea matInput cdkTextareaAutosize cdkAutosizeMinRows="1" cdkAutosizeMaxRows="5" formControlName="explanation" [placeholder]="getExplanationPlaceholder(question)"></textarea>
                </mat-form-field>
              </div>
            </div>
          }
        </div>
        <div class="add-question-btn-wrapper">
          <button type="button" class="add-question-btn" (click)="addQuestion()">
            <mat-icon>add</mat-icon>
            {{ 'Quiz.AddQuestion' | translate }}
          </button>
        </div>
        <!-- Empty State / AI Prompt -->
        @if (questions.length === 0 && !cannotUseAI()) {
          <div class="ai-promo-card">
            <div class="icon-circle">
              <mat-icon>auto_awesome</mat-icon>
            </div>
            <h3>{{ 'Quiz.AiPromoTitle' | translate }}</h3>
            <p>{{ 'Quiz.AiPromoDesc' | translate }}</p>
            <button mat-flat-button color="primary" (click)="openAiSetup()">
              {{ 'Quiz.GenerateWithAI' | translate }}
            </button>
          </div>
        }
      </form>
    </div>
  }

  <!-- AI SETUP CONTENT -->
  @if (viewMode() === 'AI_SETUP') {
    <div class="content ai-setup-content custom-scrollbar">
      <form [formGroup]="aiSetupForm">
        <label class="form-label">{{ 'Quiz.AiSubjectLabel' | translate }} *</label>
        <mat-form-field appearance="fill" class="setup-field" subscriptSizing="dynamic">
          <input matInput formControlName="subject" [placeholder]="'Quiz.AiSubjectPlaceholder' | translate" />
        </mat-form-field>
        <label class="form-label">{{ 'Quiz.AiCountLabel' | translate }}</label>
        <mat-form-field appearance="fill" class="setup-field" subscriptSizing="dynamic">
          <input matInput type="number" formControlName="count" min="1" max="15" />
          @if (aiSetupForm.get('count').hasError('max')) {
            <mat-error>
              {{ 'Quiz.MaxQuestionsHint' | translate: { max: 15 } }}
            </mat-error>
          }
        </mat-form-field>
        <div class="flex items-center gap-3 my-3">
          <label class="form-label mb-0">{{ ('Quiz.AiTypeLabel' | translate) || 'Quiz Type' }}</label>
          <ui-select formControlName="quizType" [options]="quizTypeOptions" [compact]="true"></ui-select>
        </div>
        <label class="form-label">{{ 'Quiz.AiDifficultyLabel' | translate }}</label>
        <div class="difficulty-selector">
          @for (diff of difficultyOptions; track diff.value) {
            <button type="button" [class.selected]="aiSetupForm.get('difficulty').value === diff.value" (click)="setDifficulty(diff.value)">
              {{ diff.label | translate }}
            </button>
          }
        </div>
        <label class="form-label">{{ 'Quiz.AiContextLabel' | translate }}</label>
        <mat-form-field appearance="fill" class="setup-field context-field" subscriptSizing="dynamic">
          <textarea matInput cdkTextareaAutosize cdkAutosizeMinRows="4" cdkAutosizeMaxRows="8" formControlName="context" [placeholder]="'Quiz.AiContextPlaceholder' | translate"></textarea>
        </mat-form-field>

        <!-- Attached Files -->
        @if (attachedFiles().length > 0) {
          <div class="attached-files-container mt-3">
            @for (attachment of attachedFiles(); track attachment.id) {
              @if (attachment.type === 'audio') {
                <app-voice-note-card [uploading]="attachment.uploading" [audioFile]="attachment.file" (removed)="removeAttachment(attachment.id)" class="mb-2" />
              } @else {
                <!-- Regular File Tile -->
                <div class="media-context-card mb-2 flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div class="flex items-center gap-3">
                    <div class="icon-wrapper flex items-center justify-center w-10 h-10 rounded-lg bg-indigo-50 text-indigo-500">
                      @if (attachment.uploading) {
                        <mat-spinner diameter="20"></mat-spinner>
                      } @else {
                        <mat-icon>{{ attachment.type === 'image' ? 'image' : attachment.type === 'video' ? 'videocam' : 'attach_file' }}</mat-icon>
                      }
                    </div>
                    <div class="info-wrapper flex flex-col">
                      <h4 [matTooltip]="attachment.name" class="m-0 text-sm font-medium max-w-[200px] truncate">{{ attachment.name }}</h4>
                      <p class="m-0 text-xs text-gray-500">{{ attachment.uploading ? ('FileUploader.Uploading' | translate) + '...' : ('ContentGenerator.FileAttached' | translate) }}</p>
                    </div>
                  </div>
                  <button type="button" mat-icon-button class="remove-btn" (click)="removeAttachment(attachment.id)">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
              }
            }
          </div>
        }

        <!-- Attached External Contexts -->
        @if (attachedExternalContexts() && attachedExternalContexts().length > 0) {
          <div class="attached-files-container mt-3">
            @for (ext of attachedExternalContexts(); track ext.id) {
              <div class="media-context-card mb-2 flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div class="flex items-center gap-3">
                  <div class="icon-wrapper flex items-center justify-center w-10 h-10 rounded-lg bg-indigo-50 text-indigo-500">
                    <mat-icon>{{ ext.type === 'lecture' ? 'school' : ext.type === 'note' ? 'sticky_note_2' : 'article' }}</mat-icon>
                  </div>
                  <div class="info-wrapper flex flex-col">
                    <h4 [matTooltip]="ext.title" class="m-0 text-sm font-medium max-w-[200px] truncate">{{ ext.title }}</h4>
                    <p class="m-0 text-xs text-gray-500">{{ 'ContentGenerator.FileAttached' | translate }}</p>
                  </div>
                </div>
                <button type="button" mat-icon-button class="remove-btn" (click)="removeExternalContext(ext.id)">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            }
          </div>
        }

        <div class="file-actions flex gap-2 flex-wrap mt-3 mb-4">
          <button type="button" mat-stroked-button (click)="quizFileInput.click()">
            <mat-icon>attach_file</mat-icon>
            {{ 'ContentGenerator.AttachFile' | translate }}
          </button>
          <button type="button" mat-stroked-button (click)="toggleContentSearcher()">
            <mat-icon>docs_add_on</mat-icon>
            {{ 'Searcher.AttachLibraryContent' | translate }}
          </button>
          <input type="file" #quizFileInput hidden multiple (change)="onFileSelected($event)" />
        </div>

        <!-- Voice Recording Mic Button -->
        <div class="flex justify-center mt-2">
          <app-voice-recorder-button (recorded)="onVoiceRecorded($event)" (recordingChange)="isRecordingVoice.set($event)" />
        </div>
      </form>
    </div>
  }

  <!-- AI LOADING CONTENT -->
  @if (viewMode() === 'AI_LOADING') {
    <div class="content ai-loading-content">
      <div class="loading-wrapper">
        <div class="pulsing-icon">
          <mat-icon>auto_awesome</mat-icon>
        </div>
        <h3>{{ 'Quiz.GeneratingTitle' | translate }}</h3>
        <p>{{ 'Quiz.GeneratingDesc' | translate: { count: aiSetupForm.get('count').value, subject: aiSetupForm.get('subject').value } }}</p>
        <div class="spinner-dots">
          <mat-spinner diameter="24"></mat-spinner>
        </div>
      </div>
    </div>
  }

  <!-- PREVIEW (MANUAL MODE) -->
  @if (viewMode() === 'MANUAL' && isPreviewMode()) {
    <div class="preview-container">
      <div class="preview-actions">
        <button mat-icon-button (click)="togglePreview()" class="close-preview-btn">
          <mat-icon>close</mat-icon>
        </button>
      </div>
      <div class="preview-content custom-scrollbar">
        <app-quiz [data]="previewData" [isChunkMode]="true"></app-quiz>
      </div>
    </div>
  }
  <!-- MANUAL FOOTER -->
  @if (viewMode() === 'MANUAL') {
    <div class="footer">
      <button matButton="filled" color="primary" class="insert-btn" (click)="insertQuiz()" [disabled]="isSubmitting || quizForm.invalid">
        <mat-icon>add_circle_outline</mat-icon>
        {{ 'Global.Insert' | translate }}
      </button>

      <div class="restart-link-wrapper">
        <a class="restart-link" (click)="restart()">{{ 'Global.Restart' | translate }}</a>
      </div>
    </div>
  }
  <!-- AI SETUP FOOTER -->
  @if (viewMode() === 'AI_SETUP') {
    <div class="footer">
      <button matButton="filled" color="primary" class="insert-btn" (click)="generateAiQuiz()" [disabled]="aiSetupForm.invalid || !allFilesDoneUploading()">
        {{ 'Quiz.GenerateAction' | translate }}
      </button>
      <div class="restart-link-wrapper">
        <a class="restart-link" (click)="cancelAiSetup()">{{ 'Global.Cancel' | translate }}</a>
      </div>
    </div>
  }

  @if (isTranslating()) {
    <div class="loading-overlay">
      <mat-spinner diameter="24"></mat-spinner>
      <p>{{ 'Loader.TranslationInProgress' | translate }}</p>
    </div>
  }
</div>
